import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingSkeleton } from "@app.automotus.io/components/LoadingSkeleton";
import { CitationStatusStepper } from "@app.automotus.io/components/CitationStatusStepper";
import { CitationDetails } from "@app.automotus.io/components/CitationDetails";
import { CitationImageCarousel } from "@app.automotus.io/components/CitationImageCarousel";
import VehicleInvoiceCardStatus from "@app.automotus.io/components/VehicleInvoice/VehicleInvoiceCardStatus";
import ViolationPenalty from "@app.automotus.io/components/ViolationPenalty";
import CitationGrandTotal from "@app.automotus.io/components/CitationGrandTotal";
import CitationTransaction from "@app.automotus.io/components/CitationTransaction";
import CitationTransactionRateBreakdown from "@app.automotus.io/components/CitationTransactionRateBreakdown";
import { VoidNoticeDialog } from "@curbsuite/components/VoidNoticeDialog";
import { RecordNoticePaymentDialog } from "@curbsuite/components/RecordNoticePaymentDialog";
import { CitationDetail } from "common/graphql/fragments";

export interface CitationPageContentProps {
  citationId: string;
  citation: CitationDetail | null;
  loading: boolean;
  backButtonLink: string;
  showVoidButton: boolean;
  showRecordPaymentButton: boolean;
  onVoidSubmit: (values: { reason: string; voidedAt: Date }) => void | Promise<void>;
  onRecordPaymentSubmit: (values: { paidAmountCents: number; paidAt: Date }) => void | Promise<void>;
  voidSubmitting: boolean;
  recordPaymentSubmitting: boolean;
}

export const CitationPageContent: React.FC<CitationPageContentProps> = ({
  citationId,
  citation,
  loading,
  backButtonLink,
  showVoidButton,
  showRecordPaymentButton,
  onVoidSubmit,
  onRecordPaymentSubmit,
  voidSubmitting,
  recordPaymentSubmitting,
}) => {
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [recordPaymentDialogOpen, setRecordPaymentDialogOpen] = useState(false);

  return (
    <>
      <Stack spacing={3} width={"100%"}>
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", alignItems: "center", columnGap: 1 }}>
              <IconButton component={RouterLink} to={backButtonLink} sx={{ gridRow: "1 / 3" }}>
                <ArrowBackIosIcon />
              </IconButton>
              <Box sx={{ gridColumn: 2 }}>
                <LoadingSkeleton loading={loading}>
                  <Typography variant={"h4"} fontWeight={600} letterSpacing={0.25}>
                    Citation ID: {citation?.citationNumber}
                  </Typography>
                </LoadingSkeleton>
              </Box>
              <Box sx={{ gridColumn: 2 }}>
                <LoadingSkeleton loading={loading}>
                  <Typography variant={"subtitle1"} fontSize={12} letterSpacing={0.4} color={"text.disabled"}>
                    A detailed description of a citation
                  </Typography>
                </LoadingSkeleton>
              </Box>
            </Box>
            {(showVoidButton || showRecordPaymentButton) && (
              <Stack direction="row" spacing={2}>
                {showVoidButton && (
                  <Button
                    variant="contained"
                    color="error"
                    sx={{ textTransform: "none" }}
                    onClick={() => setVoidDialogOpen(true)}
                  >
                    Void
                  </Button>
                )}
                {showRecordPaymentButton && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: "none" }}
                    onClick={() => setRecordPaymentDialogOpen(true)}
                  >
                    Record Payment
                  </Button>
                )}
              </Stack>
            )}
          </Box>
        </Box>
        {citation?.voidedAt && (
          <Box>
            <VehicleInvoiceCardStatus message={<Typography>This citation has been voided.</Typography>} />
          </Box>
        )}
        {citation?.dispute?.status === "pending" && (
          <Box>
            <VehicleInvoiceCardStatus
              status="warning"
              message={
                <span>
                  We're currently reviewing this citation. Please{" "}
                  <Link
                    sx={{ color: "inherit", textDecorationColor: "inherit" }}
                    href={`mailto:michelle@automotus.co?subject=[CurbSuite Support Request] Citation Under Review #${citation?.citationNumber}`}
                  >
                    contact us
                  </Link>{" "}
                  with questions.
                </span>
              }
            />
          </Box>
        )}
        {!(citation?.voidedAt || citation?.dispute?.status === "pending") && (
          <CitationStatusStepper citationId={citationId} />
        )}
        <CitationImageCarousel citationId={citationId} />
        <CitationDetails citationId={citationId} />
        {citation?.isSessionTransactionBillable && <CitationTransaction citationId={citationId} />}
        {citation?.isSessionTransactionBillable && (
          <CitationTransactionRateBreakdown
            loading={loading}
            policyRateDetails={citation?.sessionTransaction?.park?.policyRateDetails}
          />
        )}
        <ViolationPenalty citationId={citationId} />
        {citation?.isSessionTransactionBillable && <CitationGrandTotal citationId={citationId} />}
        {citation?.noticeContentUrl && (
          <Button target="_blank" href={citation.noticeContentUrl} variant="contained" sx={{ textTransform: "none" }}>
            View Notice PDF
          </Button>
        )}
      </Stack>
      <VoidNoticeDialog
        open={voidDialogOpen}
        onClose={() => setVoidDialogOpen(false)}
        onSubmit={async (values) => {
          try {
            await onVoidSubmit(values);
            setVoidDialogOpen(false);
          } catch {
            // Dialog stays open; error already handled by parent
          }
        }}
        entityLabel="Citation"
        isSubmitting={voidSubmitting}
      />
      <RecordNoticePaymentDialog
        open={recordPaymentDialogOpen}
        onClose={() => setRecordPaymentDialogOpen(false)}
        onSubmit={async (values) => {
          try {
            await onRecordPaymentSubmit(values);
            setRecordPaymentDialogOpen(false);
          } catch {
            // Dialog stays open; error already handled by parent
          }
        }}
        entityLabel="Citation"
        isSubmitting={recordPaymentSubmitting}
      />
    </>
  );
};

export default CitationPageContent;
